import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react'
import { Offcanvas } from 'bootstrap'

const AREA_UNIT_OPTIONS = [
  { value: 'PER_AREA_PER_YEAR',  label: 'per area / year' },
  { value: 'PER_AREA_PER_MONTH', label: 'per area / month' },
  { value: 'PER_YEAR',           label: 'per year (flat)' },
  { value: 'PER_MONTH',          label: 'per month (flat)' }
]

const PARKING_UNIT_FALLBACK = [
  { value: 'PER_UNIT_PER_YEAR',  label: 'per unit / year' },
  { value: 'PER_UNIT_PER_MONTH', label: 'per unit / month' },
  { value: 'FLAT_PER_YEAR',      label: 'per year (flat)' },
  { value: 'FLAT_PER_MONTH',     label: 'per month (flat)' }
]

const DEFAULT_AREA_UNIT    = 'PER_AREA_PER_YEAR'
const DEFAULT_PARKING_UNIT = 'PER_UNIT_PER_YEAR'

function segmentAnnualAmount(seg) {
  const area = Number(seg.area) || 0
  const rate = Number(seg.rate) || 0
  if (rate < 0) return 0
  if (seg.kind === 'PARKING') {
    switch (seg.unit) {
      case 'PER_UNIT_PER_YEAR':  return area * rate
      case 'PER_UNIT_PER_MONTH': return area * rate * 12
      case 'FLAT_PER_YEAR':      return rate
      case 'FLAT_PER_MONTH':     return rate * 12
      default: return 0
    }
  }
  switch (seg.unit) {
    case 'PER_AREA_PER_YEAR':  return area * rate
    case 'PER_AREA_PER_MONTH': return area * rate * 12
    case 'PER_YEAR':           return rate
    case 'PER_MONTH':          return rate * 12
    default: return 0
  }
}

function formatMoney(n, currency = 'GBP') {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(v)
}

const SegmentCard = memo(function SegmentCard({
  seg,
  currency,
  busy,
  areaUnitOptions,
  parkingUnitOptions,
  areaInvalid,
  onPatch,
  onSave,
  onChangeKind,
  onChangeUnit,
  onRemove
}) {
  const isParking = seg.kind === 'PARKING'
  const unitOptions = isParking ? parkingUnitOptions : areaUnitOptions
  const quantityLabel = isParking ? 'Number of units' : 'Area'
  const quantityInvalid = !isParking && areaInvalid

  return (
    <div className="p-3 border rounded">
      <div className="row g-2 mb-2">
        <div className="col-5">
          <label className="form-label small text-muted mb-1">Segment type</label>
          <select
            className="form-select form-select-sm"
            value={seg.kind || 'AREA'}
            onChange={e => onChangeKind(seg.id, e.target.value)}
            disabled={busy}
          >
            <option value="AREA">Area</option>
            <option value="PARKING">Parking</option>
          </select>
        </div>
        <div className="col-7 d-flex align-items-end">
          <div className="flex-grow-1">
            <label className="form-label small text-muted mb-1">Name</label>
            <input
              type="text"
              className="form-control form-control-sm"
              value={seg.name || ''}
              onChange={e => onPatch(seg.id, { name: e.target.value })}
              onBlur={() => onSave(seg.id)}
              disabled={busy}
            />
          </div>
          <button
            type="button"
            className="btn btn-link btn-sm text-muted ms-2"
            onClick={() => onRemove(seg.id)}
            disabled={busy}
          >
            Remove
          </button>
        </div>
      </div>

      <div className="row g-2">
        <div className="col-6">
          <label className="form-label small text-muted mb-1">{quantityLabel}</label>
          <input
            type="number"
            className={`form-control form-control-sm text-end${quantityInvalid ? ' is-invalid' : ''}`}
            value={seg.area ?? ''}
            step={isParking ? '1' : 'any'}
            min="0"
            onChange={e => {
              const raw = e.target.value
              const next = isParking ? raw.replace(/[^\d]/g, '') : raw
              onPatch(seg.id, { area: next })
            }}
            onBlur={() => onSave(seg.id)}
            disabled={busy}
          />
        </div>
        <div className="col-6">
          <label className="form-label small text-muted mb-1">Unit</label>
          <select
            className="form-select form-select-sm"
            value={seg.unit || (isParking ? DEFAULT_PARKING_UNIT : DEFAULT_AREA_UNIT)}
            onChange={e => onChangeUnit(seg.id, e.target.value)}
            disabled={busy}
          >
            {unitOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="col-6">
          <label className="form-label small text-muted mb-1">Rate</label>
          <input
            type="number"
            className="form-control form-control-sm text-end"
            value={seg.rate ?? ''}
            onChange={e => onPatch(seg.id, { rate: e.target.value })}
            onBlur={() => onSave(seg.id)}
            disabled={busy}
          />
        </div>
        <div className="col-6">
          <label className="form-label small text-muted mb-1">Segment MR pa</label>
          <div className="form-control form-control-sm bg-light text-end">
            {formatMoney(segmentAnnualAmount(seg), currency)}
          </div>
        </div>
      </div>
    </div>
  )
})

export default function AreaScheduleEditor({ currency = 'GBP' } = {}) {
  const [open, setOpen] = useState(false)
  const [tenantId, setTenantId] = useState(null)
  const [tenantName, setTenantName] = useState('')
  const [tenantArea, setTenantArea] = useState(0)
  const [segments, setSegments] = useState([])
  const [segmentsUrl, setSegmentsUrl] = useState('')
  const [csrfToken, setCsrfToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [areaUnitOptions, setAreaUnitOptions] = useState(AREA_UNIT_OPTIONS)
  const [parkingUnitOptions, setParkingUnitOptions] = useState(PARKING_UNIT_FALLBACK)

  const offcanvasRef = useRef(null)
  const bsInstanceRef = useRef(null)
  const segmentsStateRef = useRef(segments)

  useEffect(() => { segmentsStateRef.current = segments }, [segments])

  useEffect(() => {
    const handler = (event) => {
      const d = event.detail || {}
      setTenantId(d.tenantId ?? null)
      setTenantName(d.tenantName || '')
      setTenantArea(d.tenantArea ?? 0)
      setSegments(Array.isArray(d.segments) ? d.segments : [])
      setSegmentsUrl(d.segmentsUrl || '')
      setCsrfToken(d.csrfToken || '')
      if (Array.isArray(d.areaUnitOptions) && d.areaUnitOptions.length) {
        setAreaUnitOptions(d.areaUnitOptions)
      } else {
        setAreaUnitOptions(AREA_UNIT_OPTIONS)
      }
      if (Array.isArray(d.parkingUnitOptions) && d.parkingUnitOptions.length) {
        setParkingUnitOptions(d.parkingUnitOptions)
      } else {
        setParkingUnitOptions(PARKING_UNIT_FALLBACK)
      }
      setErrorMsg('')
      setOpen(true)
    }
    window.addEventListener('openAreaSchedulePanel', handler)
    return () => window.removeEventListener('openAreaSchedulePanel', handler)
  }, [])

  useEffect(() => {
    const el = offcanvasRef.current
    if (!el) return
    if (!bsInstanceRef.current) bsInstanceRef.current = new Offcanvas(el)
    if (open) {
      bsInstanceRef.current.show()
    } else {
      bsInstanceRef.current.hide()
    }
  }, [open])

  useEffect(() => {
    const el = offcanvasRef.current
    if (!el) return
    const handler = () => setOpen(false)
    el.addEventListener('hidden.bs.offcanvas', handler)
    return () => el.removeEventListener('hidden.bs.offcanvas', handler)
  }, [])

  const areaSegments    = useMemo(() => segments.filter(s => s.kind !== 'PARKING'), [segments])
  const parkingSegments = useMemo(() => segments.filter(s => s.kind === 'PARKING'), [segments])

  const areaTotal    = useMemo(() => areaSegments.reduce((acc, s) => acc + segmentAnnualAmount(s), 0),    [areaSegments])
  const parkingTotal = useMemo(() => parkingSegments.reduce((acc, s) => acc + segmentAnnualAmount(s), 0), [parkingSegments])
  const total        = areaTotal + parkingTotal

  const areaUsed    = useMemo(() => areaSegments.reduce((acc, s) => acc + (Number(s.area) || 0), 0), [areaSegments])
  const parkingUnits = useMemo(() => {
    return parkingSegments.reduce((acc, s) => {
      if (s.unit === 'PER_UNIT_PER_YEAR' || s.unit === 'PER_UNIT_PER_MONTH') {
        return acc + (Number(s.area) || 0)
      }
      return acc
    }, 0)
  }, [parkingSegments])

  const tenantAreaNum = Number(tenantArea) || 0
  const areaOverflow  = tenantAreaNum > 0 && areaUsed > tenantAreaNum

  const patchSegmentLocal = useCallback((id, patch) => {
    setSegments(list => list.map(s => (s.id === id ? { ...s, ...patch } : s)))
  }, [])

  const saveSegmentById = useCallback(async (id) => {
    if (!segmentsUrl) return
    const seg = segmentsStateRef.current.find(s => s.id === id)
    if (!seg) return
    setBusy(true)
    setErrorMsg('')
    try {
      const response = await fetch(`${segmentsUrl}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          update: {
            name: seg.name,
            kind: seg.kind || 'AREA',
            area: seg.area,
            unit: seg.unit,
            rate: seg.rate
          }
        })
      })
      const result = await response.json()
      if (result.success) {
        patchSegmentLocal(id, { annual_amount: result.segment.annual_amount })
      } else {
        setErrorMsg(describeError(result))
      }
    } catch (err) {
      console.error('saveSegmentById error', err)
      setErrorMsg('Failed to save segment')
    } finally {
      setBusy(false)
    }
  }, [segmentsUrl, csrfToken, patchSegmentLocal])

  const addSegmentOfKind = useCallback(async (kind) => {
    if (!segmentsUrl) return
    setBusy(true)
    setErrorMsg('')
    try {
      const isParking = kind === 'PARKING'
      const newPayload = {
        name: isParking
          ? `Parking ${segmentsStateRef.current.filter(s => s.kind === 'PARKING').length + 1}`
          : `Segment ${segmentsStateRef.current.filter(s => s.kind !== 'PARKING').length + 1}`,
        kind,
        area: 0,
        unit: isParking ? DEFAULT_PARKING_UNIT : DEFAULT_AREA_UNIT,
        rate: 0
      }
      const response = await fetch(segmentsUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ create: newPayload })
      })
      const result = await response.json()
      if (result.success) {
        setSegments(list => [...list, result.segment])
      } else {
        setErrorMsg(describeError(result))
      }
    } catch (err) {
      console.error('addSegment error', err)
      setErrorMsg('Failed to add segment')
    } finally {
      setBusy(false)
    }
  }, [segmentsUrl, csrfToken])

  const removeSegment = useCallback(async (id) => {
    if (!segmentsUrl) return
    setBusy(true)
    setErrorMsg('')
    try {
      const response = await fetch(`${segmentsUrl}/${id}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken }
      })
      const result = await response.json()
      if (result.success) {
        setSegments(list => list.filter(s => s.id !== id))
      } else if (result.error === 'last_segment') {
        setErrorMsg(
          'Cannot remove the last segment while Source is MR Breakdown. ' +
          'Add another segment first or change the Market Rent Source.'
        )
      } else {
        setErrorMsg(describeError(result))
      }
    } catch (err) {
      console.error('removeSegment error', err)
      setErrorMsg('Failed to remove segment')
    } finally {
      setBusy(false)
    }
  }, [segmentsUrl, csrfToken])

  const handleUnitChange = useCallback((id, nextUnit) => {
    setSegments(list => list.map(s => (s.id === id ? { ...s, unit: nextUnit } : s)))
    requestAnimationFrame(() => saveSegmentById(id))
  }, [saveSegmentById])

  const handleKindChange = useCallback((id, nextKind) => {
    setSegments(list => list.map(s => {
      if (s.id !== id) return s
      const nextUnit = nextKind === 'PARKING' ? DEFAULT_PARKING_UNIT : DEFAULT_AREA_UNIT
      return { ...s, kind: nextKind, unit: nextUnit }
    }))
    requestAnimationFrame(() => saveSegmentById(id))
  }, [saveSegmentById])

  return (
    <div
      ref={offcanvasRef}
      className="offcanvas offcanvas-end"
      tabIndex={-1}
      id="areaSchedulePanel"
      aria-labelledby="areaSchedulePanelLabel"
      style={{ width: '600px' }}
    >
      <div className="offcanvas-header border-bottom">
        <h5 className="offcanvas-title" id="areaSchedulePanelLabel">
          Market Rent Breakdown
        </h5>
        <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close" />
      </div>

      {/* EVA-135: fixed (non-scrolling) zone — tenant card, alerts and the
          Segments toolbar stay visible while the segment list scrolls. */}
      <div className="p-3 border-bottom d-flex flex-column" style={{ gap: '1rem' }}>
        <div className="p-3 rounded bg-light border">
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <div className="fw-semibold">{tenantName || '—'}</div>
              <div className="small mt-1">
                <span className={areaOverflow ? 'text-danger' : 'text-muted'}>
                  Area used: {areaUsed.toLocaleString()}
                  {tenantAreaNum > 0 ? ` of ${tenantAreaNum.toLocaleString()} sq ft` : ' sq ft'}
                </span>
                {parkingUnits > 0 && (
                  <span className="text-muted ms-3">Parking: {parkingUnits.toLocaleString()} units</span>
                )}
              </div>
            </div>
            <div className="text-end">
              <div className="text-muted small">Market Rent pa</div>
              <div className="fw-semibold">{formatMoney(total, currency)}</div>
            </div>
          </div>
        </div>

        {errorMsg && (
          <div className="alert alert-danger py-2 mb-0 small">{errorMsg}</div>
        )}

        {areaOverflow && (
          <div className="alert alert-warning py-2 mb-0 small">
            Sum of Area segments ({areaUsed.toLocaleString()}) exceeds tenant area ({tenantAreaNum.toLocaleString()} sq ft).
            Parking segments aren't counted.
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Segments</h6>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => addSegmentOfKind('AREA')}
              disabled={busy || !segmentsUrl}
            >
              <i className="bi bi-building" /> Add segment
            </button>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => addSegmentOfKind('PARKING')}
              disabled={busy || !segmentsUrl}
            >
              <i className="bi bi-car-front" /> Add parking
            </button>
          </div>
        </div>
      </div>

      <div className="offcanvas-body d-flex flex-column" style={{ gap: '1rem' }}>
        {segments.length === 0 && (
          <div className="text-muted text-center py-4">
            No segments yet. Use the buttons above to add an area or parking segment.
          </div>
        )}

        <div className="d-flex flex-column" style={{ gap: '0.75rem' }}>
          {segments.map(seg => (
            <SegmentCard
              key={seg.id}
              seg={seg}
              currency={currency}
              busy={busy}
              areaUnitOptions={areaUnitOptions}
              parkingUnitOptions={parkingUnitOptions}
              areaInvalid={areaOverflow}
              onPatch={patchSegmentLocal}
              onSave={saveSegmentById}
              onChangeKind={handleKindChange}
              onChangeUnit={handleUnitChange}
              onRemove={removeSegment}
            />
          ))}
        </div>
      </div>

      <div className="border-top p-3 d-flex justify-content-between align-items-center">
        <div>
          <div className="text-muted small">Total Market Rent pa</div>
          <div className="fw-semibold fs-5">{formatMoney(total, currency)}</div>
          {areaTotal > 0 && parkingTotal > 0 && (
            <div className="text-muted small">
              Area {formatMoney(areaTotal, currency)} + Parking {formatMoney(parkingTotal, currency)}
            </div>
          )}
        </div>
        <button type="button" className="btn btn-primary" data-bs-dismiss="offcanvas">
          Apply &amp; Close
        </button>
      </div>
    </div>
  )
}

function describeError(result) {
  if (!result) return 'Unknown error'
  if (result.error === 'validation_error' && result.errors) {
    return Object.entries(result.errors)
      .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
      .join('; ')
  }
  return result.error || 'Unknown error'
}
